import { useRef } from "react";
import { motion, useScroll, useTransform } from "framer-motion";
import { Link } from "react-router-dom";
import { ASSETS, CONTACT } from "../lib/brand";

const LINES = ["Moda", "que", "renasce."];

const lineVariant = {
  hidden: { y: "110%" },
  visible: (i) => ({
    y: 0,
    transition: {
      duration: 1.1,
      ease: [0.22, 1, 0.36, 1],
      delay: 0.35 + i * 0.14,
    },
  }),
};

export default function Hero() {
  const ref = useRef(null);
  const { scrollYProgress } = useScroll({
    target: ref,
    offset: ["start start", "end start"],
  });
  const y = useTransform(scrollYProgress, [0, 1], [0, 220]);
  const scale = useTransform(scrollYProgress, [0, 1], [1, 1.1]);
  const opacity = useTransform(scrollYProgress, [0, 1], [1, 0.4]);

  return (
    <section
      id="top"
      ref={ref}
      data-testid="hero-section"
      className="relative min-h-[100svh] w-full overflow-hidden pt-28 sm:pt-32 lg:pt-36 pb-10"
    >
      {/* Meta bar */}
      <motion.div
        initial={{ opacity: 0 }}
        animate={{ opacity: 1 }}
        transition={{ delay: 0.6, duration: 0.8 }}
        className="mx-auto max-w-[1600px] px-6 sm:px-10 flex items-center justify-between text-[10px] font-mono uppercase tracking-[0.3em] text-ink-800/60"
      >
        <span>N.º 001 — Cap. Aberto</span>
        <span className="hidden sm:inline">Moda circular · Curitiba</span>
        <span>Vol. MMXXVI</span>
      </motion.div>

      <div className="mx-auto max-w-[1600px] px-6 sm:px-10 mt-8 lg:mt-14 grid grid-cols-12 gap-6 lg:gap-10 items-start">
        {/* Headline */}
        <div className="col-span-12 lg:col-span-7 relative">
          <h1
            data-testid="hero-headline"
            className="font-serif text-brick-500 leading-[0.86] tracking-tight"
            style={{ fontSize: "clamp(4.5rem, 14vw, 15rem)" }}
          >
            {LINES.map((line, i) => (
              <span key={i} className="mask-line">
                <motion.span
                  variants={lineVariant}
                  initial="hidden"
                  animate="visible"
                  custom={i}
                  className={i === 2 ? "italic font-medium" : "font-normal"}
                >
                  {line}
                </motion.span>
              </span>
            ))}
          </h1>

          <motion.p
            initial={{ opacity: 0, y: 20 }}
            animate={{ opacity: 1, y: 0 }}
            transition={{ delay: 1, duration: 0.9, ease: [0.22, 1, 0.36, 1] }}
            className="mt-10 max-w-md font-sans text-base sm:text-lg leading-relaxed text-ink-800/80"
          >
            A voz que redefine o que é <span className="italic-serif text-brick-500">moda circular</span> no Brasil.
            Curadoria, consultoria e presença — assinadas por mim.
          </motion.p>

          <motion.div
            initial={{ opacity: 0, y: 20 }}
            animate={{ opacity: 1, y: 0 }}
            transition={{ delay: 1.2, duration: 0.8 }}
            className="mt-10 flex flex-wrap items-center gap-4"
          >
            <a
              href={CONTACT.whatsapp}
              target="_blank"
              rel="noreferrer"
              data-testid="hero-cta-primary"
              className="btn-brick"
            >
              Falar com a Herida
            </a>
            <Link
              to="/proposito"
              data-testid="hero-cta-secondary"
              className="btn-ghost"
            >
              Ler o propósito
            </Link>
          </motion.div>

          <motion.div
            initial={{ opacity: 0 }}
            animate={{ opacity: 1 }}
            transition={{ delay: 1.5, duration: 0.8 }}
            className="mt-16 flex items-center gap-6 font-mono text-[10px] uppercase tracking-[0.3em] text-ink-800/60"
          >
            <span>↓ Role para descobrir</span>
            <span className="w-24 h-px bg-ink-800/20" />
            <span>03 capítulos</span>
          </motion.div>
        </div>

        {/* Image */}
        <div className="col-span-12 lg:col-span-5 relative lg:mt-4">
          <motion.div
            initial={{ y: 60, opacity: 0 }}
            animate={{ y: 0, opacity: 1 }}
            transition={{ delay: 0.5, duration: 1.3, ease: [0.22, 1, 0.36, 1] }}
            style={{ y, scale, opacity }}
            className="relative w-full aspect-[3/4] overflow-hidden bg-cream-200"
          >
            <img
              src={ASSETS.hero}
              alt="Herida — curadora de moda circular"
              className="absolute inset-0 w-full h-full object-cover"
              data-testid="hero-image"
            />
            {/* Spotlight overlay */}
            <div
              aria-hidden
              className="absolute inset-0 pointer-events-none"
              style={{
                background:
                  "radial-gradient(60% 55% at 55% 45%, rgba(255,255,255,0) 0%, rgba(26,24,24,0.25) 100%)",
              }}
            />
            {/* Frame ticks */}
            <div className="absolute top-3 left-3 right-3 flex justify-between font-mono text-[9px] uppercase tracking-[0.3em] text-cream mix-blend-difference">
              <span>Herida</span>
              <span>001 / IV</span>
            </div>
            <div className="absolute bottom-3 left-3 right-3 flex justify-between font-mono text-[9px] uppercase tracking-[0.3em] text-cream mix-blend-difference">
              <span>Retrato — Editorial</span>
              <span>MMXXVI</span>
            </div>
          </motion.div>

          <motion.div
            initial={{ opacity: 0 }}
            animate={{ opacity: 1 }}
            transition={{ delay: 1.4, duration: 0.8 }}
            className="mt-6 flex items-start gap-4"
          >
            <span className="mt-1 block w-8 h-px bg-brick-500" />
            <p className="font-sans text-xs sm:text-sm leading-relaxed text-ink-800/70 max-w-xs">
              <span className="italic-serif text-brick-500">Herida</span> — voz da moda circular no Brasil,
              membro do BNI, presença constante na mídia.
            </p>
          </motion.div>
        </div>
      </div>
    </section>
  );
}
